import { SaveOutlined } from "@ant-design/icons";
import { App, Button, Drawer, Form, Space } from "antd";
import { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { cleanupTempAttachments } from "../../api/attachmentApi";
import { createEntry, getEntry, updateEntry } from "../../api/entryApi";
import { AttachmentPanel } from "../../shared/components/AttachmentPanel";
import { getApiErrorMessage } from "../../shared/utils/apiError";
import type { EntryPageConfig, DailySummaryRow } from "./entryConfigs";
import { CashEntryFields, DailyEntryFields, StatementEntryFields, type LookupLabels } from "./EntryUpsertFields";
import { toCashPayload, toDailyPayload, toStatementPayload } from "./entryUpsertPayloads";

export function EntryUpsertDrawer<T extends DailySummaryRow>({
  open,
  config,
  editId,
  onClose,
  onSaved
}: {
  open: boolean;
  config: EntryPageConfig<T>;
  editId?: string | number | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [labels, setLabels] = useState<LookupLabels>({});
  const [tempKey, setTempKey] = useState(() => crypto.randomUUID());
  const mode = editId ? "edit" : "create";
  const paymentType = Form.useWatch("paymentType", form);
  const drawerTitle = `${mode === "edit" ? "Edit" : "Create"} ${config.title}`;

  const initialValues = useMemo(() => {
    if (config.key === "CashEntry") {
      return { dated: dayjs(), debit: 0, credit: 0 };
    }
    if (config.key === "StatementEntry") {
      return { tranDate: dayjs(), debit: 0, credit: 0, type: "Debit" };
    }
    return {
      paymentEntryDate: dayjs(),
      paymentPaidDate: dayjs(),
      transactionType: "Debit",
      paymentType: "Cash",
      paymentMode: "NA",
      paymentStatus: "Paid",
      expenseMonthPayment: dayjs().format("MMMM")
    };
  }, [config.key]);

  useEffect(() => {
    if (!open) return;
    form.resetFields();
    form.setFieldsValue(initialValues);
    setLabels({});
    if (!editId) setTempKey(crypto.randomUUID());
    if (!editId || !config.detailUrl) return;

    let active = true;
    setLoading(true);
    getEntry<Record<string, any>>(config.detailUrl, editId)
      .then((data) => {
        if (!active || !data) return;
        hydrateEntryForm(config.key, form, data, setLabels);
      })
      .catch((err) => message.error(getApiErrorMessage(err, `Unable to load ${config.title}`)))
      .finally(() => setLoading(false));

    return () => { active = false; };
  }, [config.detailUrl, config.key, config.title, editId, form, initialValues, message, open]);

  const save = async () => {
    if (!config.saveUrl) return;
    const values = await form.validateFields();
    if ((config.key === "CashEntry" || config.key === "StatementEntry") && Number(values.debit ?? 0) <= 0 && Number(values.credit ?? 0) <= 0) {
      message.warning("Debit or Credit must be greater than zero.");
      return;
    }
    const payload = config.key === "CashEntry"
      ? toCashPayload(values)
      : config.key === "StatementEntry"
        ? toStatementPayload(values)
        : toDailyPayload(values, editId ? undefined : tempKey);

    setSaving(true);
    try {
      if (editId) await updateEntry(config.saveUrl, editId, payload);
      else await createEntry(config.saveUrl, payload);
      message.success(`${config.title} saved`);
      onSaved();
      onClose();
    } catch (err) {
      message.error(getApiErrorMessage(err, `Unable to save ${config.title}`));
    } finally {
      setSaving(false);
    }
  };

  const cancel = async () => {
    if (config.key === "DailyEntry" && !editId && tempKey) {
      await cleanupTempAttachments([tempKey]).catch(() => undefined);
    }
    onClose();
  };

  return (
    <Drawer
      title={drawerTitle}
      open={open}
      onClose={cancel}
      width={config.key === "CashEntry" ? 560 : 760}
      className="entry-upsert-drawer"
      zIndex={1400}
    >
      <Form form={form} layout="vertical" initialValues={initialValues} disabled={loading}>
        {config.key === "CashEntry" ? (
          <CashEntryFields form={form} labels={labels} setLabels={setLabels} />
        ) : config.key === "StatementEntry" ? (
          <StatementEntryFields form={form} labels={labels} setLabels={setLabels} />
        ) : (
          <DailyEntryFields form={form} labels={labels} setLabels={setLabels} paymentType={paymentType} />
        )}
        {config.key === "DailyEntry" ? (
          <div className="drawer-attachment-block">
            <AttachmentPanel module="DailyEntry" entityId={editId ?? null} tempKey={editId ? null : tempKey} />
          </div>
        ) : null}
      </Form>
      <div className="drawer-sticky-actions">
        <Space>
          <Button onClick={cancel}>Cancel</Button>
          <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={save}>Save</Button>
        </Space>
      </div>
    </Drawer>
  );
}

function hydrateEntryForm(
  key: EntryPageConfig<DailySummaryRow>["key"],
  form: ReturnType<typeof Form.useForm>[0],
  data: Record<string, any>,
  setLabels: (labels: LookupLabels) => void
) {
  if (key === "CashEntry") {
    form.setFieldsValue({
      dated: data.dated ? dayjs(data.dated) : dayjs(),
      paidOf: data.paidOf,
      paymentOf: data.paymentOf,
      debit: data.debit ?? 0,
      credit: data.credit ?? 0,
      remarks: data.remarks
    });
    setLabels({ paidOf: data.paidOf, paymentOf: data.paymentOf });
    return;
  }
  if (key === "StatementEntry") {
    form.setFieldsValue({
      tranDate: data.tranDate ? dayjs(data.tranDate) : dayjs(),
      chequeNo: data.chequeNo,
      particulars: data.particulars,
      debit: data.debit ?? 0,
      credit: data.credit ?? 0,
      narration: data.narration,
      type: data.type ?? (Number(data.debit ?? 0) > 0 ? "Debit" : "Credit")
    });
    setLabels({ chequeNo: data.chequeNo, particulars: data.particulars });
    return;
  }

  form.setFieldsValue({
    paymentEntryDate: data.paymentEntryDate ? dayjs(data.paymentEntryDate) : dayjs(),
    paymentPaidDate: data.paymentPaidDate ? dayjs(data.paymentPaidDate) : null,
    paidOfId: data.paidOfId,
    paymentOfId: data.paymentOfId,
    shortRemarkId: data.shortRemarkId,
    longRemarks: data.longRemarks,
    expenseMonthPayment: data.expenseMonthPayment ?? dayjs().format("MMMM"),
    accountLedgerId: data.accountLedgerId ?? data.entryCategoryId,
    amount: data.amount,
    transactionType: normalizeTransactionType(data.transactionTypeName ?? data.transactionType),
    paymentType: normalizePaymentType(data.paymentTypeName ?? data.paymentType),
    bankId: data.bankId,
    bankAccountId: data.bankAccountId,
    paymentMode: data.paymentMode ?? "Cash",
    paymentReference: data.paymentReference,
    paymentStatus: normalizePaymentStatus(data.paymentStatus)
  });
  setLabels({
    paidOf: data.paidOfName,
    paymentOf: data.paymentOfName,
    shortRemark: data.shortRemarkName,
    entryCategory: data.entryCategoryName ?? data.accountLedgerName ?? data.expenseTypeName,
    bank: data.bankName,
    bankAccount: data.bankAccountNumber ?? data.bankAccountName
  });
}

function normalizePaymentStatus(value: unknown) {
  if (value === 2 || value === "2" || value === "Paid") return "Paid";
  if (value === 3 || value === "3" || value === "Failed") return "Failed";
  if (value === 4 || value === "4" || value === "Cancelled") return "Cancelled";
  return "Pending";
}

function normalizePaymentType(value: unknown) {
  if (value === 2 || value === "2" || value === "Bank") return "Bank";
  return "Cash";
}

function normalizeTransactionType(value: unknown) {
  if (value === 2 || value === "2" || value === "Credit") return "Credit";
  return "Debit";
}
